import { create } from 'zustand'
import {
  fetchPreferences,
  savePreferences,
  type KanbanStageOrderMap,
  type SidebarOrderMap,
  type SidebarSectionCollapsedMap,
  type SidebarSectionOrderList,
  type TableCollapsedGroupsMap,
  type TableColumnsMap,
  type TableGroupByMap,
  type UserPreferences,
} from '@/lib/api/preferences'
import type { NavSection } from '@/shell/module-system'

export type Theme = 'light' | 'dark' | 'system'
export type Density = 'comfortable' | 'compact' | 'spacious'

interface PreferencesState extends UserPreferences {
  hydrated: boolean
  setTheme: (theme: Theme) => void
  setDensity: (density: Density) => void
  setSidebarCollapsed: (collapsed: boolean) => void
  /** Sprint 13a — toggle a single section's collapsed state. */
  setSectionCollapsed: (section: NavSection, collapsed: boolean) => void
  /** Sprint 13b — set the user's ordering for a single section. */
  setSidebarOrder: (section: NavSection, ids: ReadonlyArray<string>) => void
  /** Sprint 13b — clear all per-section ordering (Reset action). */
  resetSidebarOrder: () => void
  /** Set the user's ordering for the top-level sidebar categories. */
  setSidebarSectionOrder: (ids: ReadonlyArray<string>) => void
  /**
   * Clear the category ordering (Reset action). Independent of
   * `resetSidebarOrder` — neither reset touches the other's data.
   */
  resetSidebarSectionOrder: () => void
  /**
   * Sprint 17 — write a single module's column sizing (id → px).
   * Merges with any existing `order` for the module, replaces sizing
   * wholesale (TanStack passes the full sizing map on each change).
   */
  setTableColumnSizing: (moduleId: string, sizing: Record<string, number>) => void
  /**
   * Sprint 17 — write a single module's column order. Replaces wholesale.
   */
  setTableColumnOrder: (moduleId: string, order: ReadonlyArray<string>) => void
  /**
   * Sprint 17 — clear a single module's column state (Reset columns
   * action in the module's toolbar overflow menu). Omitting `moduleId`
   * clears the entire map — wired to a future global "reset all tables"
   * surface; not used by the per-module reset.
   */
  resetTableColumns: (moduleId?: string) => void
  /**
   * Sprint 22 hot-fix — set the user's kanban column order for a
   * single moduleId (e.g., 'sales-inbox'). Replaces wholesale.
   */
  setKanbanStageOrder: (moduleId: string, order: ReadonlyArray<string>) => void
  /**
   * Sprint 22 hot-fix — clear a single module's kanban order (Reset
   * action) or omit `moduleId` to clear all.
   */
  resetKanbanStageOrder: (moduleId?: string) => void
  /** Set the "Group by" field for a module's table view ('none' clears grouping). */
  setTableGroupBy: (moduleId: string, value: string) => void
  /** Replace the collapsed group-key set for a module's grouped table. */
  setTableCollapsedGroups: (moduleId: string, keys: ReadonlyArray<string>) => void
  hydrate: () => Promise<void>
}

const DEFAULTS: UserPreferences = {
  theme: 'system',
  density: 'comfortable',
  sidebarCollapsed: false,
  sidebarSectionsCollapsed: {},
  sidebarOrder: {},
  sidebarSectionOrder: [],
  tableColumns: {},
  kanbanStageOrder: {},
  tableGroupBy: {},
  tableCollapsedGroups: {},
}

let persistTimer: ReturnType<typeof setTimeout> | null = null

function schedulePersist(state: UserPreferences) {
  if (persistTimer) clearTimeout(persistTimer)
  persistTimer = setTimeout(() => {
    void savePreferences(state)
  }, 500)
}

export const usePreferences = create<PreferencesState>((set, get) => ({
  ...DEFAULTS,
  hydrated: false,

  setTheme: (theme) => {
    set({ theme })
    schedulePersist(currentSnapshot(get()))
  },

  setDensity: (density) => {
    set({ density })
    schedulePersist(currentSnapshot(get()))
  },

  setSidebarCollapsed: (sidebarCollapsed) => {
    set({ sidebarCollapsed })
    schedulePersist(currentSnapshot(get()))
  },

  setSectionCollapsed: (section, collapsed) => {
    const next: SidebarSectionCollapsedMap = {
      ...get().sidebarSectionsCollapsed,
      [section]: collapsed,
    }
    set({ sidebarSectionsCollapsed: next })
    schedulePersist(currentSnapshot(get()))
  },

  setSidebarOrder: (section, ids) => {
    const next: SidebarOrderMap = {
      ...get().sidebarOrder,
      [section]: [...ids],
    }
    set({ sidebarOrder: next })
    schedulePersist(currentSnapshot(get()))
  },

  resetSidebarOrder: () => {
    set({ sidebarOrder: {} })
    schedulePersist(currentSnapshot(get()))
  },

  setSidebarSectionOrder: (ids) => {
    const next: SidebarSectionOrderList = [...ids]
    set({ sidebarSectionOrder: next })
    schedulePersist(currentSnapshot(get()))
  },

  resetSidebarSectionOrder: () => {
    set({ sidebarSectionOrder: [] })
    schedulePersist(currentSnapshot(get()))
  },

  setTableColumnSizing: (moduleId, sizing) => {
    const existing = get().tableColumns[moduleId]
    const next: TableColumnsMap = {
      ...get().tableColumns,
      [moduleId]: {
        order: existing?.order ?? [],
        sizing: { ...sizing },
      },
    }
    set({ tableColumns: next })
    schedulePersist(currentSnapshot(get()))
  },

  setTableColumnOrder: (moduleId, order) => {
    const existing = get().tableColumns[moduleId]
    const next: TableColumnsMap = {
      ...get().tableColumns,
      [moduleId]: {
        order: [...order],
        sizing: existing?.sizing ?? {},
      },
    }
    set({ tableColumns: next })
    schedulePersist(currentSnapshot(get()))
  },

  resetTableColumns: (moduleId) => {
    if (moduleId === undefined) {
      set({ tableColumns: {} })
    } else {
      const next: TableColumnsMap = { ...get().tableColumns }
      delete next[moduleId]
      set({ tableColumns: next })
    }
    schedulePersist(currentSnapshot(get()))
  },

  setKanbanStageOrder: (moduleId, order) => {
    const next: KanbanStageOrderMap = {
      ...get().kanbanStageOrder,
      [moduleId]: [...order],
    }
    set({ kanbanStageOrder: next })
    schedulePersist(currentSnapshot(get()))
  },

  resetKanbanStageOrder: (moduleId) => {
    if (moduleId === undefined) {
      set({ kanbanStageOrder: {} })
    } else {
      const next: KanbanStageOrderMap = { ...get().kanbanStageOrder }
      delete next[moduleId]
      set({ kanbanStageOrder: next })
    }
    schedulePersist(currentSnapshot(get()))
  },

  setTableGroupBy: (moduleId, value) => {
    const next: TableGroupByMap = { ...get().tableGroupBy, [moduleId]: value }
    set({ tableGroupBy: next })
    schedulePersist(currentSnapshot(get()))
  },

  setTableCollapsedGroups: (moduleId, keys) => {
    const next: TableCollapsedGroupsMap = {
      ...get().tableCollapsedGroups,
      [moduleId]: [...keys],
    }
    set({ tableCollapsedGroups: next })
    schedulePersist(currentSnapshot(get()))
  },

  hydrate: async () => {
    const loaded = await fetchPreferences()
    set({ ...DEFAULTS, ...loaded, hydrated: true })
  },
}))

function currentSnapshot(state: PreferencesState): UserPreferences {
  return {
    theme: state.theme,
    density: state.density,
    sidebarCollapsed: state.sidebarCollapsed,
    sidebarSectionsCollapsed: state.sidebarSectionsCollapsed,
    sidebarOrder: state.sidebarOrder,
    sidebarSectionOrder: state.sidebarSectionOrder,
    tableColumns: state.tableColumns,
    kanbanStageOrder: state.kanbanStageOrder,
    tableGroupBy: state.tableGroupBy,
    tableCollapsedGroups: state.tableCollapsedGroups,
  }
}
